import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, PieChart as PieIcon, Users, Sparkles, FileDown } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { api, errMsg } from "../lib/api";
import { PageHeader, FullLoader, EmptyState } from "../components/common";

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="rounded-2xl bg-card border border-border p-4">
    <div className={`flex items-center gap-1.5 text-xs font-semibold ${tint}`}><Icon size={14} /> {label}</div>
    <div className="amount-figure text-2xl mt-1.5">{value}</div>
  </div>
);

export default function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [statements, setStatements] = useState([]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/insights"); setData(r.data); }
      catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
    api.get("/insights/summary").then((r) => setSummary(r.data.summary)).catch(() => {});
    api.get("/statements").then((r) => setStatements(r.data.statements)).catch(() => {});
  }, []);

  const backend = process.env.REACT_APP_BACKEND_URL;

  if (loading) return <FullLoader />;
  if (!data) return <EmptyState icon={PieIcon} title="Couldn't load insights" body="Please try again in a moment." />;

  const chartData = (data.monthly || []).map((m) => ({ month: m.month, In: m.in / 100, Out: m.out / 100 }));

  return (
    <div className="animate-fade-up">
      <PageHeader title="Insights" subtitle="Where your money goes" />

      {summary && (
        <div data-testid="ai-summary-card" className="rounded-3xl p-5 mb-6 border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5">
          <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-sm"><Sparkles size={16} /> Your money coach</div>
          <p className="text-[15px] leading-relaxed text-foreground/90">{summary}</p>
        </div>
      )}

      {!data.has_data ? (
        <EmptyState icon={PieIcon} title="Nothing to analyze yet"
          body="Once you start sending, receiving and saving, your spending insights will appear here." />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={TrendingDown} label="Spent this month" value={data.this_month_out_display} tint="text-destructive" />
            <Stat icon={TrendingUp} label="Added this month" value={data.this_month_in_display} tint="text-success" />
          </div>

          <div className="rounded-3xl bg-card border border-border p-5">
            <h3 className="font-semibold mb-1">Last 6 months</h3>
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-success inline-block" /> Money in</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-primary inline-block" /> Money out</span>
            </div>
            <div style={{ width: "100%", height: 200 }} data-testid="insights-chart">
              <ResponsiveContainer>
                <BarChart data={chartData} barGap={4}>
                  <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} stroke="#94A3B8" />
                  <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} formatter={(v) => `$${v.toFixed(2)}`}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                  <Bar dataKey="In" fill="#16A34A" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Out" fill="#4B23CC" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {data.categories.length > 0 && (
            <div className="rounded-3xl bg-card border border-border p-5">
              <h3 className="font-semibold mb-3">Spending breakdown</h3>
              <div className="flex items-center gap-5">
                <div style={{ width: 120, height: 120 }} className="shrink-0" data-testid="insights-donut">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={data.categories} dataKey="amount" nameKey="label" innerRadius={34} outerRadius={56} paddingAngle={2}>
                        {data.categories.map((c) => <Cell key={c.label} fill={c.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  {data.categories.map((c) => (
                    <div key={c.label} className="flex items-center gap-2 text-sm" data-testid={`insights-cat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                      <span className="flex-1 truncate">{c.label}</span>
                      <span className="text-muted-foreground">{c.percent}%</span>
                      <span className="font-mono font-medium w-20 text-right">{c.amount_display}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {data.top_recipients.length > 0 && (
            <div className="rounded-3xl bg-card border border-border p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Users size={16} /> Top recipients</h3>
              <div className="space-y-2">
                {data.top_recipients.map((r) => (
                  <div key={r.name} className="flex items-center justify-between text-sm">
                    <span className="truncate">{r.name}</span>
                    <span className="font-mono font-medium">{r.amount_display}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {statements.length > 0 && (
        <div className="rounded-3xl bg-card border border-border p-5 mt-6" data-testid="statements-section">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><FileDown size={16} /> Monthly statements</h3>
          <div className="divide-y divide-border">
            {statements.map((s) => (
              <a key={s.ym} href={`${backend}/api/statements/${s.ym}`} target="_blank" rel="noopener noreferrer"
                data-testid={`statement-${s.ym}`}
                className="flex items-center justify-between py-3 group">
                <span className="text-sm font-medium">{s.label}</span>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-primary"><FileDown size={15} /> PDF</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
